import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import type { ReactNode } from 'react';

/**
 * Themed modal provider. Replaces native window.confirm / window.prompt /
 * window.alert with three hooks: useConfirm / usePrompt / useAlert. Every
 * dialog renders with the ARMS chrome (sidebar-blue header bar, white
 * title + close X, brand-blue primary button, error-red destructive
 * button) so it visibly belongs to the product.
 *
 * Use:
 *   const confirm = useConfirm();
 *   if (!await confirm({ title: 'Promote release · sc-app@1.4.2',
 *                        message: 'draft → in_test?',
 *                        confirmLabel: 'Promote' })) return;
 *
 *   const prompt = usePrompt();
 *   const reason = await prompt({ title: 'Deprecation reason',
 *                                  placeholder: 'optional',
 *                                  multiline: true });
 *   if (reason === null) return;          // user cancelled
 *   // empty string ≠ null — empty means "submitted blank"
 *
 *   const alert = useAlert();
 *   await alert({ title: 'Download failed',
 *                 message: err.message, severity: 'error' });
 */

// Brand palette — pulled from AppShell.tsx so dialogs feel native.
const BRAND_BLUE = '#1f3b6f';
const BRAND_BLUE_DARK = '#152748';

export type ConfirmOptions = {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

export type PromptOptions = {
  title: string;
  message?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

export type AlertOptions = {
  title: string;
  message: ReactNode;
  severity?: 'info' | 'warning' | 'error' | 'success';
  closeLabel?: string;
};

type ConfirmRequest  = { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean)        => void };
type PromptRequest   = { kind: 'prompt';  opts: PromptOptions;  resolve: (v: string | null) => void };
type AlertRequest    = { kind: 'alert';   opts: AlertOptions;   resolve: ()                  => void };
type DialogRequest   = ConfirmRequest | PromptRequest | AlertRequest;

type DialogApi = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt:  (opts: PromptOptions)  => Promise<string | null>;
  alert:   (opts: AlertOptions)   => Promise<void>;
};

const DialogContext = createContext<DialogApi | null>(null);

function useDialogApi(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useConfirm/usePrompt/useAlert must be used inside <DialogProvider>');
  return ctx;
}

export const useConfirm = () => useDialogApi().confirm;
export const usePrompt  = () => useDialogApi().prompt;
export const useAlert   = () => useDialogApi().alert;

export function DialogProvider({ children }: { children: ReactNode }) {
  // Single-active model — nested calls stack, only the top one renders.
  const [stack, setStack] = useState<DialogRequest[]>([]);
  const [promptValue, setPromptValue] = useState('');

  const push = useCallback(<T,>(req: DialogRequest, defaultPromptValue = ''): Promise<T> => {
    return new Promise<T>((res) => {
      (req as unknown as { resolve: (v: T) => void }).resolve = res;
      setPromptValue(defaultPromptValue);
      setStack((s) => [...s, req]);
    });
  }, []);

  const api = useMemo<DialogApi>(() => ({
    confirm: (opts) => push<boolean>({ kind: 'confirm', opts, resolve: () => {} } as ConfirmRequest),
    prompt:  (opts) => push<string | null>(
      { kind: 'prompt', opts, resolve: () => {} } as PromptRequest,
      opts.defaultValue ?? '',
    ),
    alert:   (opts) => push<void>({ kind: 'alert', opts, resolve: () => {} } as AlertRequest),
  }), [push]);

  const top = stack[stack.length - 1];

  const close = useCallback(() => setStack((s) => s.slice(0, -1)), []);

  const handleConfirm = useCallback(() => {
    if (!top) return;
    if (top.kind === 'confirm') top.resolve(true);
    else if (top.kind === 'prompt') top.resolve(promptValue);
    else if (top.kind === 'alert') top.resolve();
    close();
  }, [top, promptValue, close]);

  const handleCancel = useCallback(() => {
    if (!top) return;
    if (top.kind === 'confirm') top.resolve(false);
    else if (top.kind === 'prompt') top.resolve(null);
    else if (top.kind === 'alert') top.resolve();
    close();
  }, [top, close]);

  return (
    <DialogContext.Provider value={api}>
      {children}
      {top && (
        <ThemedDialog
          req={top}
          promptValue={promptValue}
          setPromptValue={setPromptValue}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Renderer — Style A (solid brand header)
// ─────────────────────────────────────────────────────────────────────────
//
// Sidebar-blue (#1f3b6f) header bar with the title in white and a close
// X on the right. Body has 24px padding, white background, black text.
// Footer with separator, brand-blue primary button (error-red when
// destructive). minWidth 520px so version-suffixed titles like
// "Promote release · sc-app@0.0.1-agent-test-1" don't wrap.

type ThemedDialogProps = {
  req: DialogRequest;
  promptValue: string;
  setPromptValue: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

function ThemedDialog({ req, promptValue, setPromptValue, onConfirm, onCancel }: ThemedDialogProps) {
  const labels = actionLabels(req);
  // Copy button on alerts — house rule "every error surface must let
  // the user grab the message verbatim" (see ErrorAlert.tsx). Available
  // on all alert severities, not just 'error', so users can also grab
  // confirmation codes / IDs out of info / success popups.
  const copyableText = req.kind === 'alert' ? extractCopyText(req.opts) : null;
  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 520,
          maxWidth: 640,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          bgcolor: BRAND_BLUE,
          color: '#fff',
          px: 3, py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flex: 1,
            fontSize: 17,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {req.opts.title}
        </Typography>
        {copyableText && (
          <CopyButton text={copyableText} />
        )}
        {labels.cancel && (
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              color: '#fff',
              opacity: 0.85,
              '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <DialogBody
          req={req}
          promptValue={promptValue}
          setPromptValue={setPromptValue}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        {labels.cancel && <Button onClick={onCancel}>{labels.cancel}</Button>}
        <Button
          onClick={onConfirm}
          variant="contained"
          color={labels.destructive ? 'error' : 'primary'}
          autoFocus
          sx={{
            minWidth: 96,
            ...(labels.destructive
              ? {}
              : { bgcolor: BRAND_BLUE, '&:hover': { bgcolor: BRAND_BLUE_DARK } }),
          }}
        >
          {labels.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Body content — varies by dialog kind. Confirm/alert just render the
// message; prompt also renders the TextField + handles Enter/Escape.
function DialogBody({ req, promptValue, setPromptValue, onConfirm, onCancel }: ThemedDialogProps) {
  if (req.kind === 'alert' || req.kind === 'confirm') {
    return (
      <Typography variant="body2" component="div" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
        {req.opts.message}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {req.opts.message && (
        <Typography variant="body2" component="div" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
          {req.opts.message}
        </Typography>
      )}
      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder={req.opts.placeholder}
        value={promptValue}
        onChange={(e) => setPromptValue(e.target.value)}
        multiline={req.opts.multiline}
        minRows={req.opts.multiline ? 3 : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !req.opts.multiline) {
            e.preventDefault();
            onConfirm();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    </Stack>
  );
}

function actionLabels(req: DialogRequest): { confirm: string; cancel: string | null; destructive: boolean } {
  if (req.kind === 'alert') {
    return { confirm: req.opts.closeLabel ?? 'OK', cancel: null, destructive: false };
  }
  return {
    confirm: req.opts.confirmLabel ?? (req.kind === 'prompt' ? 'OK' : 'Confirm'),
    cancel:  req.opts.cancelLabel  ?? 'Cancel',
    destructive: !!(req.opts as ConfirmOptions | PromptOptions).destructive,
  };
}

/**
 * Best-effort flatten of an alert's {title, message} into copyable text.
 * Falls back through ReactNode → string via String() so even an Alert
 * built from JSX still produces something useful in the clipboard.
 */
function extractCopyText(opts: AlertOptions): string {
  const parts: string[] = [];
  if (opts.title)   parts.push(opts.title.toString().trim());
  if (opts.message != null) {
    const msg = nodeToText(opts.message).trim();
    if (msg) parts.push(msg);
  }
  return parts.join('\n').trim();
}

function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  // ReactElement — recurse into its children if available; otherwise stringify.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const el = node as any;
  if (el && typeof el === 'object' && el.props && 'children' in el.props) {
    return nodeToText(el.props.children);
  }
  return String(node);
}

/**
 * Copy-to-clipboard pill in the dialog header. Tooltip flips to "Copied"
 * for 1.5s after a successful write. Silent on clipboard-permission
 * errors — no error chain is more user-hostile than a copy-button that
 * pops a dialog when it fails.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy message'}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label="Copy message"
        sx={{
          color: '#fff',
          opacity: 0.85,
          '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.12)' },
        }}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}
